import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { breakpoints } from '../../../theme/index';

interface HeroProps {
  $backgroundImage: string; // Transient prop for background image
}

const colors = {
  // Example fallback colors if your CSS variables are unavailable:
  darkOverlay: 'rgba(0, 0, 0, 0.5)',
  buttonFocus: '#FFD700', // bright gold for focus outline, adjust as needed
};

/* 1) GlassOverlay - ensure enough background contrast; remove pure red fallback 
   2) Use a darker fallback if 'backdrop-filter' is unsupported */
export const GlassOverlay = styled.div`
  @supports not (backdrop-filter: blur(8px)) {
  background: rgba(0, 0, 0, 0.6); /* Darker background for contrast */
}

  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  border-right: 1px solid rgba(255, 255, 255, 0.18);
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  position: relative;
  z-index: 1;
  overflow: hidden;

  @media (max-width: ${breakpoints.tablet}) {
    padding-top: 100px;
    min-height: 60vh;
  }

  @media (max-width: ${breakpoints.mobile}) {
    min-height: 70vh;
  }

  @media (orientation: landscape) and (max-width: ${breakpoints.tablet}) {
    min-height: 70vh;
  }

  @media (orientation: landscape) and (max-width: ${breakpoints.mobile}) {
    min-height: 50vh;
  }
`;

export const HeroContainer = styled.section<HeroProps>`
  background-image: url(${(props) => props.$backgroundImage});
  background-size: cover;
  background-position: center;
  color: var(--color-text);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 85vh;
  overflow: hidden;

  /* 2) Dark overlay for better text contrast */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${colors.darkOverlay};
    z-index: 0; 
  }

  @media (max-width: ${breakpoints.tablet}) {
    height: auto;
    min-height: 60vh;
    padding: 0;
  }

  @media (max-width: ${breakpoints.mobile}) {
    min-height: 70vh;
  }

  @media (orientation: landscape) and (max-width: ${breakpoints.tablet}) {
    min-height: 50vh;
  }

  @media (orientation: landscape) and (max-width: ${breakpoints.mobile}) {
    min-height: 50vh;
  }
`;

export const ContentWrapper = styled.div`
  position: relative; 
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  z-index: 2; /* Above the overlay */

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
  }
`;

const fadeInWithDelay = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* 3) Headings: ensure color contrast is high enough */
export const TextContent = styled.div`
  flex: 1;
  text-align: center;
  max-width: 900px;

  .hero-heading {
    margin-bottom: 1.5rem;
    line-height: 1.2;
    font-family: 'Montserrat', sans-serif;
    font-weight: bold;
    color: var(--color-text-light);

    span {
      display: block;
      opacity: 0;
      animation: ${fadeInWithDelay} 1s ease-out forwards;

      /* 4) If you need text to be more fluid, 
         consider removing white-space: nowrap; if you used it */
      &:nth-child(1) {
        font-size: clamp(2rem, 4vw, 3.5rem);
        animation-delay: 0s;
      }

      &:nth-child(2) {
        font-size: clamp(1.5rem, 3vw, 2.5rem);
        animation-delay: 0.3s;
        color: var(--color-secondary);
      }

      &:nth-child(3) {
        font-size: clamp(2rem, 3.5vw, 3rem);
        font-family: Bahnschrift, 'DIN Alternate', 
                     'Franklin Gothic Medium', 'Nimbus Sans Narrow', 
                     sans-serif-condensed, sans-serif;
        animation-delay: 0.6s;
        color: var(--color-highlight);
      }
    }
  }
    .hero-heading span {
  &:nth-child(1) {
    color: rgba(255, 255, 255, 0.87); /* Adjust for better contrast */
  }

  &:nth-child(2) {
    color: rgba(255, 255, 255, 0.87);
  }

  &:nth-child(3) {
    color: rgba(255, 200, 0, 1); /* Highlight color adjusted for contrast */
  }
}


  @media (max-width: ${breakpoints.tablet}) {
    .hero-heading {
      span:nth-child(1) {
        font-size: 2rem;
      }
      span:nth-child(2) {
        font-size: 1.8rem;
      }
      span:nth-child(3) {
        font-size: 2rem;
      }
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 700px;
    margin-right: 1rem;

    .hero-heading {
      span:nth-child(1) {
        font-size: 1.5rem;
      }
      span:nth-child(2) {
        font-size: 1.2rem;
      }
      span:nth-child(3) {
        font-size: 1.8rem;
      }
    }
  }
`;

/* 5) Image alt text should be descriptive (shown below in the component). */
export const ImageWrapper = styled.div`
  flex: 1;
  display: flex;
  z-index: 1000;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  overflow: hidden;

  img {
    max-width: 100%;
    height: auto;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    object-fit: cover;

    @media (max-width: ${breakpoints.tablet}) {
      border-top-left-radius: 15px;
      border-top-right-radius: 10px;
    }
  }

  @media (max-width: ${breakpoints.tablet}) {
    margin-top: 1rem;
    justify-content: center;

    img {
      max-width: 70%;
    }
  }
`;

/* 6) CTA Buttons: ensure color contrast, visible :focus state, 
      and avoid relying solely on color for hover/focus feedback. */
export const CTAButton = styled(Link) <{ $primary?: boolean }>`
  padding: 0.75rem 1.5rem;
  margin: 1rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  display: inline-block;

  /* Distinct focus outline for keyboard users */
  &:focus {
    outline: 3px solid ${colors.buttonFocus};
    outline-offset: 2px;
  }

  ${(props) =>
    props.$primary
      ? `
    background-color: var(--color-new-button);
    color: #fff;

    &:hover {
      background-color: var(--color-new-button-hover);
      color: #fff;
    }
  `
      : `
    background-color: transparent;
    color: var(--color-secondary);
    border: 2px solid var(--color-secondary);

    &:hover {
      background-color: var(--color-secondary);
      color: var(--color-text);
    }
  `}
`;